import type { ValidCurrency } from '@/types/plans';

import type { Cohort } from './cohorts';

export type PricingTestPlanKey = 'essentials' | 'creator' | 'pro';

export interface CohortPlanOverride {
  /**
   * Monthly price in the currency's smallest unit (cents/pence/etc).
   * INR is stored in whole rupees (the unit Stripe charges INR in on this
   * account); see the `/100` skip in applyPricingOverridesToApiResponse.
   */
  monthlyPriceInMinor: number;
  /** Stripe SKU strings. */
  sku: { monthly: string; yearly: string };
  /** Numeric Stripe plan ID. */
  stripePlanId: number;
  yearlyPriceInMinor: number;
}

export type CohortOverride = Record<PricingTestPlanKey, CohortPlanOverride>;

/**
 * Override prices/SKUs/Stripe IDs per (currency-bucket, cohort).
 *
 * Control cohorts have no entry — the baseline (Stripe IDs 31/32/33, with the
 * Phase-1-winner prices configured in Stripe per currency) is used unchanged.
 *
 * EUR and GBP only have a Moderate cohort in Phase 2; the Aggressive cohort
 * has no defined prices for those currencies (per Phase 2 Prices CSV).
 */
export const COHORT_CONFIG: {
  EUR: { moderate: CohortOverride };
  GBP: { moderate: CohortOverride };
  USD_US: { moderate: CohortOverride; aggressive: CohortOverride };
  USD_ROW: { moderate: CohortOverride; aggressive: CohortOverride };
  AUD: { moderate: CohortOverride; aggressive: CohortOverride };
  BRL: { moderate: CohortOverride; aggressive: CohortOverride };
  INR: { reduced: CohortOverride; lowest: CohortOverride };
} = {
  EUR: {
    moderate: {
      essentials: {
        stripePlanId: 35,
        sku: { monthly: 'UBESSBMN06', yearly: 'UBESSBAN06' },
        monthlyPriceInMinor: 899,
        yearlyPriceInMinor: 8388,
      },
      creator: {
        stripePlanId: 38,
        sku: { monthly: 'UBCRSBMN06', yearly: 'UBCRSBAN06' },
        monthlyPriceInMinor: 1299,
        yearlyPriceInMinor: 10_788,
      },
      pro: {
        stripePlanId: 41,
        sku: { monthly: 'UBPOSBMN07', yearly: 'UBPOSBAN07' },
        monthlyPriceInMinor: 2799,
        yearlyPriceInMinor: 19_188,
      },
    },
  },
  GBP: {
    moderate: {
      essentials: {
        stripePlanId: 35,
        sku: { monthly: 'UBESSBMN06', yearly: 'UBESSBAN06' },
        monthlyPriceInMinor: 899,
        yearlyPriceInMinor: 8388,
      },
      creator: {
        stripePlanId: 38,
        sku: { monthly: 'UBCRSBMN06', yearly: 'UBCRSBAN06' },
        monthlyPriceInMinor: 1299,
        yearlyPriceInMinor: 10_788,
      },
      pro: {
        stripePlanId: 41,
        sku: { monthly: 'UBPOSBMN07', yearly: 'UBPOSBAN07' },
        monthlyPriceInMinor: 2499,
        yearlyPriceInMinor: 17_988,
      },
    },
  },
  USD_US: {
    moderate: {
      essentials: {
        stripePlanId: 35,
        sku: { monthly: 'UBESSBMN06', yearly: 'UBESSBAN06' },
        monthlyPriceInMinor: 899,
        yearlyPriceInMinor: 8388,
      },
      creator: {
        stripePlanId: 38,
        sku: { monthly: 'UBCRSBMN06', yearly: 'UBCRSBAN06' },
        monthlyPriceInMinor: 1299,
        yearlyPriceInMinor: 10_788,
      },
      pro: {
        stripePlanId: 41,
        sku: { monthly: 'UBPOSBMN07', yearly: 'UBPOSBAN07' },
        monthlyPriceInMinor: 2499,
        yearlyPriceInMinor: 17_988,
      },
    },
    aggressive: {
      essentials: {
        stripePlanId: 36,
        sku: { monthly: 'UBESSBMN07', yearly: 'UBESSBAN07' },
        monthlyPriceInMinor: 999,
        yearlyPriceInMinor: 9588,
      },
      creator: {
        stripePlanId: 39,
        sku: { monthly: 'UBCRSBMN07', yearly: 'UBCRSBAN07' },
        monthlyPriceInMinor: 1499,
        yearlyPriceInMinor: 11_988,
      },
      pro: {
        stripePlanId: 42,
        sku: { monthly: 'UBPOSBMN08', yearly: 'UBPOSBAN08' },
        monthlyPriceInMinor: 3199,
        yearlyPriceInMinor: 19_188,
      },
    },
  },
  USD_ROW: {
    moderate: {
      essentials: {
        stripePlanId: 35,
        sku: { monthly: 'UBESSBMN06', yearly: 'UBESSBAN06' },
        monthlyPriceInMinor: 899,
        yearlyPriceInMinor: 8388,
      },
      creator: {
        stripePlanId: 38,
        sku: { monthly: 'UBCRSBMN06', yearly: 'UBCRSBAN06' },
        monthlyPriceInMinor: 1299,
        yearlyPriceInMinor: 10_788,
      },
      pro: {
        stripePlanId: 41,
        sku: { monthly: 'UBPOSBMN07', yearly: 'UBPOSBAN07' },
        monthlyPriceInMinor: 2499,
        yearlyPriceInMinor: 17_988,
      },
    },
    aggressive: {
      essentials: {
        stripePlanId: 36,
        sku: { monthly: 'UBESSBMN07', yearly: 'UBESSBAN07' },
        monthlyPriceInMinor: 999,
        yearlyPriceInMinor: 9588,
      },
      creator: {
        stripePlanId: 39,
        sku: { monthly: 'UBCRSBMN07', yearly: 'UBCRSBAN07' },
        monthlyPriceInMinor: 1499,
        yearlyPriceInMinor: 11_988,
      },
      pro: {
        stripePlanId: 42,
        sku: { monthly: 'UBPOSBMN08', yearly: 'UBPOSBAN08' },
        monthlyPriceInMinor: 3199,
        yearlyPriceInMinor: 19_188,
      },
    },
  },
  AUD: {
    moderate: {
      essentials: {
        stripePlanId: 35,
        sku: { monthly: 'UBESSBMN06', yearly: 'UBESSBAN06' },
        monthlyPriceInMinor: 1299,
        yearlyPriceInMinor: 11_988,
      },
      creator: {
        stripePlanId: 38,
        sku: { monthly: 'UBCRSBMN06', yearly: 'UBCRSBAN06' },
        monthlyPriceInMinor: 1799,
        yearlyPriceInMinor: 14_988,
      },
      pro: {
        stripePlanId: 41,
        sku: { monthly: 'UBPOSBMN07', yearly: 'UBPOSBAN07' },
        monthlyPriceInMinor: 3599,
        yearlyPriceInMinor: 25_788,
      },
    },
    aggressive: {
      essentials: {
        stripePlanId: 36,
        sku: { monthly: 'UBESSBMN07', yearly: 'UBESSBAN07' },
        monthlyPriceInMinor: 1399,
        yearlyPriceInMinor: 13_188,
      },
      creator: {
        stripePlanId: 39,
        sku: { monthly: 'UBCRSBMN07', yearly: 'UBCRSBAN07' },
        monthlyPriceInMinor: 1999,
        yearlyPriceInMinor: 16_788,
      },
      pro: {
        stripePlanId: 42,
        sku: { monthly: 'UBPOSBMN08', yearly: 'UBPOSBAN08' },
        monthlyPriceInMinor: 4499,
        yearlyPriceInMinor: 27_588,
      },
    },
  },
  BRL: {
    moderate: {
      essentials: {
        stripePlanId: 35,
        sku: { monthly: 'UBESSBMN06', yearly: 'UBESSBAN06' },
        monthlyPriceInMinor: 4499,
        yearlyPriceInMinor: 41_988,
      },
      creator: {
        stripePlanId: 38,
        sku: { monthly: 'UBCRSBMN06', yearly: 'UBCRSBAN06' },
        monthlyPriceInMinor: 6499,
        yearlyPriceInMinor: 53_988,
      },
      pro: {
        stripePlanId: 41,
        sku: { monthly: 'UBPOSBMN07', yearly: 'UBPOSBAN07' },
        monthlyPriceInMinor: 12_499,
        yearlyPriceInMinor: 89_988,
      },
    },
    aggressive: {
      essentials: {
        stripePlanId: 36,
        sku: { monthly: 'UBESSBMN07', yearly: 'UBESSBAN07' },
        monthlyPriceInMinor: 4999,
        yearlyPriceInMinor: 47_988,
      },
      creator: {
        stripePlanId: 39,
        sku: { monthly: 'UBCRSBMN07', yearly: 'UBCRSBAN07' },
        monthlyPriceInMinor: 7499,
        yearlyPriceInMinor: 59_988,
      },
      pro: {
        stripePlanId: 42,
        sku: { monthly: 'UBPOSBMN08', yearly: 'UBPOSBAN08' },
        monthlyPriceInMinor: 15_999,
        yearlyPriceInMinor: 95_988,
      },
    },
  },
  INR: {
    reduced: {
      essentials: {
        stripePlanId: 43,
        sku: { monthly: 'UBESSBMN08', yearly: 'UBESSBAN08' },
        monthlyPriceInMinor: 499,
        yearlyPriceInMinor: 4788,
      },
      creator: {
        stripePlanId: 44,
        sku: { monthly: 'UBCRSBMN08', yearly: 'UBCRSBAN08' },
        monthlyPriceInMinor: 799,
        yearlyPriceInMinor: 7188,
      },
      pro: {
        stripePlanId: 45,
        sku: { monthly: 'UBPOSBMN09', yearly: 'UBPOSBAN09' },
        monthlyPriceInMinor: 1999,
        yearlyPriceInMinor: 14_388,
      },
    },
    lowest: {
      essentials: {
        stripePlanId: 46,
        sku: { monthly: 'UBESSBMN09', yearly: 'UBESSBAN09' },
        monthlyPriceInMinor: 399,
        yearlyPriceInMinor: 3588,
      },
      creator: {
        stripePlanId: 47,
        sku: { monthly: 'UBCRSBMN09', yearly: 'UBCRSBAN09' },
        monthlyPriceInMinor: 699,
        yearlyPriceInMinor: 5388,
      },
      pro: {
        stripePlanId: 48,
        sku: { monthly: 'UBPOSBMN10', yearly: 'UBPOSBAN10' },
        monthlyPriceInMinor: 1999,
        yearlyPriceInMinor: 11_988,
      },
    },
  },
};

export type CurrencyBucket = keyof typeof COHORT_CONFIG;

const US_AND_TERRITORIES = new Set(['US', 'PR', 'VI', 'GU', 'AS', 'MP', 'UM']);

export function getCurrencyBucket(
  currency: ValidCurrency,
  country: string
): CurrencyBucket | null {
  if (currency === 'CAD') {
    return null;
  }
  if (currency === 'EUR') {
    return 'EUR';
  }
  if (currency === 'GBP') {
    return 'GBP';
  }
  if (currency === 'INR') {
    return 'INR';
  }
  if (currency === 'AUD') {
    return 'AUD';
  }
  if (currency === 'BRL') {
    return 'BRL';
  }
  // Without a usable country we cannot decide between USD-US and USD-ROW;
  // returning `null` opts the user out of the test entirely rather than
  // silently bucketing every missing-geo user into USD-ROW.
  if (!country) {
    return null;
  }
  return US_AND_TERRITORIES.has(country.toUpperCase()) ? 'USD_US' : 'USD_ROW';
}

/**
 * Reverse-lookup of `COHORT_CONFIG`: given the user's billed Stripe plan ID,
 * derive the cohort they were assigned to when they subscribed.
 *
 * The plan ID is the source of truth for what the user actually paid for —
 * Stripe charged them on this plan, and `COHORT_CONFIG` is a 1-to-1 mapping
 * from (bucket, cohort, tier) to plan ID. Inverting it for paid users lets
 * downstream surfaces (upgrade modal, switch-to-yearly, other tier cards on
 * `/pricing`, etc.) render Phase-2 cohort prices instead of the GrowthBook
 * `control` fallback that kicks in once `subscriptionType` flips away from
 * `"Free"` and the targeting condition stops matching.
 *
 * Returns `null` when the plan ID isn't present in `COHORT_CONFIG` — i.e.
 * Phase-1 baseline plans (31/32/33) and legacy / pre-Phase-2 plans. Callers
 * should fall back to the normal GrowthBook lookup in that case (which will
 * resolve to `'control'` for the converted-user audience).
 *
 * The returned cohort is bucket-agnostic: a single plan ID can map to the
 * same cohort across multiple currency buckets (e.g. plan 36 is
 * `'aggressive'` in USD_US, USD_ROW, AUD, BRL). The backend currency-locks a
 * user to a single currency at purchase time (Stripe doesn't allow multiple
 * countries per customer), so the (currency, country) the caller sees on the
 * settled session is the same one the user paid in — no bucket-mismatch
 * scenario in practice.
 */
export function getCohortFromPlanId(planId: number): Cohort | null {
  const tiers: PricingTestPlanKey[] = ['essentials', 'creator', 'pro'];
  for (const bucketConfig of Object.values(COHORT_CONFIG)) {
    for (const [cohort, override] of Object.entries(bucketConfig) as [
      Cohort,
      CohortOverride,
    ][]) {
      for (const tier of tiers) {
        if (override[tier]?.stripePlanId === planId) {
          return cohort;
        }
      }
    }
  }
  return null;
}

/**
 * Returns the override for a given (currency, country, cohort) tuple, or null
 * for control cohorts and currencies with no test (CAD).
 */
export function getCohortOverride(
  currency: ValidCurrency,
  country: string,
  cohort: Cohort
): CohortOverride | null {
  if (cohort === 'control') {
    return null;
  }
  const bucket = getCurrencyBucket(currency, country);
  if (!bucket) {
    return null;
  }

  const bucketConfig = COHORT_CONFIG[bucket] as Record<
    string,
    CohortOverride | undefined
  >;
  return bucketConfig[cohort] ?? null;
}
